import {
  OBJECT_CARD_GENERAL_FIELDS,
  ObjectCardBlockKey,
  ObjectCardGeneralContentType,
  ObjectCardGeneralFieldKey,
  ObjectCardSettingsType,
} from './object-card.type';

/**
 * Порядок блоков карточки по умолчанию — применяется, когда у пользователя ещё нет настроек.
 * general всегда первый.
 */
export const DEFAULT_OBJECT_CARD_BLOCKS_ORDER: ObjectCardBlockKey[] = [
  'general',
  'maintenance',
  'sensors',
  'drivers',
  'geozones',
  'trailers',
  'connectionSettings',
  'customFields',
  'params',
  'specifications',
];

const GENERAL_FIELDS_SET = new Set<string>(OBJECT_CARD_GENERAL_FIELDS);

function isGeneralField(value: unknown): value is ObjectCardGeneralFieldKey {
  return typeof value === 'string' && GENERAL_FIELDS_SET.has(value);
}

/** Настройки по умолчанию: все блоки видимы, у general видимы все поля. */
export function getDefaultObjectCardSettings(): ObjectCardSettingsType {
  return DEFAULT_OBJECT_CARD_BLOCKS_ORDER.reduce<ObjectCardSettingsType>((acc, key, index) => {
    acc[key] = {
      index,
      content: key === 'general' ? [...OBJECT_CARD_GENERAL_FIELDS] : [],
    };
    return acc;
  }, {});
}

/**
 * Приводит настройки карточки из профиля к формату AXENTA-2596 `{ index, content }`.
 *
 * Понимает и старый плоский формат (`{ general: 0, sensors: 1 }`) — фронт и бэкенд
 * раскатываются не одновременно, а карточка не должна разъезжаться в переходный момент.
 * У старого формата general получает полный набор полей: раньше они были захардкожены в шаблоне.
 *
 * Блок general присутствует в результате всегда и всегда стоит на позиции 0 — он не скрывается.
 */
export function normalizeObjectCardSettings(raw: unknown): ObjectCardSettingsType {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return getDefaultObjectCardSettings();
  }

  const source = raw as Record<string, unknown>;
  const normalized: ObjectCardSettingsType = {};

  DEFAULT_OBJECT_CARD_BLOCKS_ORDER.forEach((key, fallbackIndex) => {
    const value = source[key];

    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'number') {
      normalized[key] = {
        index: value,
        content: key === 'general' ? [...OBJECT_CARD_GENERAL_FIELDS] : [],
      };
      return;
    }

    if (typeof value === 'object') {
      const block = value as { index?: unknown; content?: unknown };
      const index = typeof block.index === 'number' ? block.index : fallbackIndex;
      const content = Array.isArray(block.content) ? block.content : [];

      normalized[key] = {
        index,
        content: key === 'general' ? content.filter(isGeneralField) : [],
      };
    }
  });

  // general не скрывается: если бэкенд его не прислал — показываем с одним именем объекта.
  normalized.general = { index: 0, content: normalized.general?.content ?? [] };

  return normalized;
}

/** Ключи блоков в порядке отображения. general всегда первый. */
export function getObjectCardBlocksOrder(settings: ObjectCardSettingsType): ObjectCardBlockKey[] {
  const rest = (Object.keys(settings) as ObjectCardBlockKey[])
    .filter((key) => key !== 'general')
    .sort((a, b) => (settings[a]?.index ?? 0) - (settings[b]?.index ?? 0));

  return ['general', ...rest];
}

/** Флаги видимости полей general для использования в шаблоне. */
export function getObjectCardGeneralContent(
  settings: ObjectCardSettingsType,
): ObjectCardGeneralContentType {
  const content = new Set(settings.general?.content ?? []);

  return OBJECT_CARD_GENERAL_FIELDS.reduce((acc, field) => {
    acc[field] = content.has(field);
    return acc;
  }, {} as ObjectCardGeneralContentType);
}
